import { AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { userPool } from '../auth/cognito';
import { useAuth } from '../auth/AuthProvider';
import { AuthLayout } from '../components/layout/AuthLayout';
import { PasswordField } from '../components/auth/PasswordField';
import '../styles/activation.css';

type InvitationChallenge = { user: CognitoUser };

export function LoginPage() {
  const { session, setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<InvitationChallenge | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (session) return <Navigate to="/panel" replace />;
  if (challenge) return <InvitationActivation challenge={challenge} onAuthenticated={(newSession) => { setSession(newSession); navigate('/panel'); }} />;

  function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError('');
    const normalizedEmail = email.trim().toLowerCase();
    const user = new CognitoUser({ Username: normalizedEmail, Pool: userPool });
    // Password auth handles Cognito email aliases consistently during the
    // FORCE_CHANGE_PASSWORD invitation flow. SRP remains enabled in the pool.
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');
    user.authenticateUser(new AuthenticationDetails({ Username: normalizedEmail, Password: password }), {
      onSuccess: (newSession) => { setSubmitting(false); setSession(newSession); navigate('/panel'); },
      onFailure: (failure) => { setSubmitting(false); setError(describeLoginError(failure)); },
      newPasswordRequired: () => { setSubmitting(false); setChallenge({ user }); },
    });
  }

  return <AuthLayout eyebrow="Panel de control" title={<>Bienvenido<br />de vuelta.</>} description="Administra visitas, clientes y recompensas desde un solo lugar.">
    <form className="auth-form" onSubmit={submit}>
      {params.get('activated') === '1' && <p className="auth-notice">Tu pago está confirmado. Entra para comenzar.</p>}
      <label>Correo electrónico<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="tu@negocio.mx" required autoComplete="email" autoFocus /></label>
      <PasswordField label="Contraseña" value={password} onChange={setPassword} autoComplete="current-password" />
      {error && <p className="auth-error">{error}</p>}
      <button className="auth-primary" disabled={submitting}>{submitting ? 'Entrando…' : 'Entrar al panel'} <span>→</span></button>
      <Link className="auth-link" to="/recuperar">¿Olvidaste tu contraseña?</Link>
      <Link className="auth-link" to="/registro">¿No tienes cuenta? Regístrate</Link>
    </form>
  </AuthLayout>;
}

function InvitationActivation({ challenge, onAuthenticated }: { challenge: InvitationChallenge; onAuthenticated: (session: CognitoUserSession) => void }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    const problem = validatePassword(password);
    if (problem) return setError(problem);
    if (password !== confirmation) return setError('Las contraseñas no coinciden.');
    setSubmitting(true); setError('');
    challenge.user.completeNewPasswordChallenge(password, {}, {
      onSuccess: onAuthenticated,
      onFailure: (failure) => { setSubmitting(false); setError(describeCognitoError(failure)); },
    });
  }

  return <AuthLayout eyebrow="Activa tu invitación" title={<>Crea tu<br />contraseña.</>} description="Esta será la contraseña que usarás para administrar tu negocio en Argox.">
    <form className="auth-form" onSubmit={submit}>
      <PasswordField label="Nueva contraseña" value={password} onChange={setPassword} autoComplete="new-password" autoFocus hint={<small>Mínimo 10 caracteres, con mayúscula, minúscula y número.</small>} />
      <PasswordField label="Confirmar contraseña" value={confirmation} onChange={setConfirmation} autoComplete="new-password" />
      {error && <p className="auth-error">{error}</p>}
      <button className="auth-primary" disabled={submitting}>{submitting ? 'Activando…' : 'Activar mi cuenta'} <span>→</span></button>
    </form>
  </AuthLayout>;
}

function validatePassword(password: string) {
  if (password.length < 10) return 'La contraseña necesita al menos 10 caracteres.';
  if (!/[A-Z]/.test(password)) return 'Agrega por lo menos una letra mayúscula.';
  if (!/[a-z]/.test(password)) return 'Agrega por lo menos una letra minúscula.';
  if (!/[0-9]/.test(password)) return 'Agrega por lo menos un número.';
  return '';
}
function describeLoginError(failure: unknown) { const error = failure as { name?: string; code?: string; message?: string }; const code = error.name ?? error.code; if (code === 'PasswordResetRequiredException') return 'Esta cuenta necesita restablecer su contraseña. Usa "¿Olvidaste tu contraseña?".'; if (code === 'NotAuthorizedException') return 'Correo o contraseña incorrectos.'; if (code === 'UserNotFoundException') return 'Correo o contraseña incorrectos.'; if (code === 'TooManyRequestsException' || code === 'LimitExceededException') return 'Demasiados intentos. Espera un momento y vuelve a intentar.'; return error.message ?? 'No pudimos iniciar sesión.'; }
function describeCognitoError(failure: unknown) { const error = failure as { name?: string; code?: string; message?: string }; const code = error.name ?? error.code; if (code === 'InvalidPasswordException') return 'La contraseña no cumple los requisitos. Revisa las reglas de abajo.'; if (code === 'NotAuthorizedException') return 'No pudimos validar esta invitación. Pide que te envíen una nueva.'; return error.message ?? 'No pudimos activar la cuenta.'; }
